import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

export const DEFAULT_REFRESH_BATCH_SIZE = 10;

const POLL_INTERVAL_MS = 2000;

function clampSize(value, maxSize) {
    const n = parseInt(String(value ?? "").trim(), 10);
    if (!Number.isFinite(n)) return DEFAULT_REFRESH_BATCH_SIZE;
    return Math.min(maxSize, Math.max(1, n));
}

function formatElapsed(ms) {
    if (!Number.isFinite(ms) || ms < 0) return "—";
    const totalSec = Math.round(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
}

/**
 * Starts and monitors a background preview-refresh sweep.
 *
 * Rendering a batch takes far longer than the gateway timeout, so the server runs it as a job and this
 * dialog polls for progress. Closing the dialog does not stop the job; reopening reattaches to it.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {React.ReactNode} props.description — what the refresh will overwrite
 * @param {number} [props.maxSize] — batch size ceiling accepted by the backend
 * @param {(size: number, opts: { runAll: boolean, afterStoryId: string }) => Promise<{ success: boolean, error?: string, data?: object }>} props.startJobFn
 * @param {(jobId: string) => Promise<{ success: boolean, error?: string, data?: object }>} props.fetchJobFn
 * @param {(jobId: string) => Promise<{ success: boolean, error?: string, data?: object }>} [props.cancelJobFn]
 * @param {() => void} [props.onComplete] — called after the dialog closes, to refresh the list
 */
export default function PreviewRefreshJobDialog({
    open,
    onClose,
    title,
    description,
    maxSize = 100,
    startJobFn,
    fetchJobFn,
    cancelJobFn,
    onComplete,
}) {
    const [sizeInput, setSizeInput] = useState(String(DEFAULT_REFRESH_BATCH_SIZE));
    const [runAll, setRunAll] = useState(false);
    const [afterStoryId, setAfterStoryId] = useState("");
    const [starting, setStarting] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [job, setJob] = useState(null);
    const [error, setError] = useState("");
    const pollTimerRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const jobId = job?.jobId ?? null;
    const running = Boolean(job) && !job.done;

    useEffect(() => {
        if (!open) {
            stopPolling();
            return;
        }
        setSizeInput(String(DEFAULT_REFRESH_BATCH_SIZE));
        setRunAll(false);
        setAfterStoryId("");
        setError("");
        setStarting(false);
        setCancelling(false);
        // Reattach to a sweep already running from an earlier visit or another tab.
        let cancelled = false;
        (async () => {
            const result = await fetchJobFn(null);
            if (cancelled) return;
            setJob(result.success && result.data && !result.data.done ? result.data : null);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, fetchJobFn, stopPolling]);

    useEffect(() => {
        if (!open || !jobId || !running) {
            stopPolling();
            return undefined;
        }
        pollTimerRef.current = setInterval(async () => {
            const result = await fetchJobFn(jobId);
            if (!result.success) {
                setError(result.error || "Lost track of the refresh job");
                return;
            }
            setJob(result.data);
        }, POLL_INTERVAL_MS);
        return stopPolling;
    }, [open, jobId, running, fetchJobFn, stopPolling]);

    useEffect(() => stopPolling, [stopPolling]);

    const handleStart = useCallback(async () => {
        const size = clampSize(sizeInput, maxSize);
        if (!runAll) {
            setSizeInput(String(size));
        }
        setStarting(true);
        setError("");
        const result = await startJobFn(size, { runAll, afterStoryId: afterStoryId.trim() });
        setStarting(false);
        if (!result.success) {
            setError(result.error || "Failed to start the refresh job");
            return;
        }
        setJob(result.data);
    }, [sizeInput, maxSize, runAll, afterStoryId, startJobFn]);

    const handleCancelJob = useCallback(async () => {
        if (!jobId || !cancelJobFn) return;
        setCancelling(true);
        const result = await cancelJobFn(jobId);
        setCancelling(false);
        if (!result.success) {
            setError(result.error || "Failed to cancel the refresh job");
            return;
        }
        setJob(result.data);
    }, [jobId, cancelJobFn]);

    const handleClose = useCallback(() => {
        stopPolling();
        setJob(null);
        setError("");
        onClose();
        if (onComplete) onComplete();
    }, [stopPolling, onClose, onComplete]);

    const total = Number(job?.total ?? 0);
    const processed = Number(job?.processed ?? 0);
    const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {error ? (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                ) : null}

                {job ? (
                    <Stack spacing={1.5}>
                        <Typography variant="body2">
                            <strong>Status:</strong> {job.status ?? "—"}
                            {job.cancelRequested && !job.done ? " (stopping after current story…)" : ""}
                        </Typography>
                        <Box>
                            <LinearProgress
                                variant={total > 0 ? "determinate" : "indeterminate"}
                                value={percent}
                                sx={{ height: 8, borderRadius: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {processed} / {total} stories ({percent}%)
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            <strong>Succeeded:</strong> {job.succeeded ?? 0} &nbsp;·&nbsp;
                            <strong>Failed:</strong> {job.failed ?? 0} &nbsp;·&nbsp;
                            <strong>Elapsed:</strong> {formatElapsed(Number(job.elapsedMs))}
                        </Typography>
                        {job.currentStoryId ? (
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                <strong>Current story:</strong> {job.currentStoryId}
                            </Typography>
                        ) : null}
                        {job.lastStoryId ? (
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                <strong>Last story:</strong> {job.lastStoryId}
                                <Typography component="span" variant="caption" color="text.secondary">
                                    {" "}
                                    (use as “resume after” if you stop here)
                                </Typography>
                            </Typography>
                        ) : null}
                        {Array.isArray(job.bundleStates) && job.bundleStates.length > 0 ? (
                            <Typography variant="body2">
                                <strong>Bundle states:</strong> {job.bundleStates.join(", ")}
                            </Typography>
                        ) : null}
                        {Array.isArray(job.errors) && job.errors.length > 0 ? (
                            <Box>
                                <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                                    Errors
                                </Typography>
                                <Box
                                    sx={{
                                        maxHeight: 160,
                                        overflow: "auto",
                                        p: 1,
                                        bgcolor: "grey.100",
                                        borderRadius: 1,
                                    }}
                                >
                                    {job.errors.map((err, idx) => (
                                        <Typography
                                            component="div"
                                            variant="body2"
                                            color="error"
                                            key={`refresh-job-err-${idx}`}
                                            sx={{ wordBreak: "break-word" }}
                                        >
                                            {String(err)}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                        {running ? (
                            <Typography variant="caption" color="text.secondary">
                                Runs on the server — you can close this dialog and come back later.
                            </Typography>
                        ) : null}
                    </Stack>
                ) : (
                    <>
                        {description ? <Typography sx={{ mt: 1 }}>{description}</Typography> : null}
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    checked={runAll}
                                    onChange={(_, checked) => setRunAll(checked)}
                                    disabled={starting}
                                    color="primary"
                                />
                            }
                            label="Refresh all existing preview images (run-all, no batch limit)"
                        />
                        <TextField
                            margin="dense"
                            label="Resume after story id (optional)"
                            fullWidth
                            value={afterStoryId}
                            onChange={(e) => setAfterStoryId(e.target.value)}
                            disabled={starting}
                            helperText="Skips through this story (inclusive), then processes the rest."
                            sx={{ mt: 1 }}
                        />
                        <TextField
                            margin="dense"
                            label="Batch size"
                            type="number"
                            fullWidth
                            value={sizeInput}
                            onChange={(e) => setSizeInput(e.target.value)}
                            disabled={starting || runAll}
                            slotProps={{ htmlInput: { min: 1, max: maxSize } }}
                            helperText={
                                runAll
                                    ? "Ignored while run-all is checked."
                                    : `Maximum stories to process (1–${maxSize}). Default ${DEFAULT_REFRESH_BATCH_SIZE}.`
                            }
                            sx={{ mt: 1 }}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {job ? (
                    <>
                        {running && cancelJobFn ? (
                            <Button onClick={handleCancelJob} color="error" disabled={cancelling || job.cancelRequested}>
                                {cancelling ? <CircularProgress size={20} /> : "Stop"}
                            </Button>
                        ) : null}
                        <Button onClick={handleClose} variant="contained">
                            Close
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={starting}>
                            Cancel
                        </Button>
                        <Button onClick={handleStart} variant="contained" color="primary" disabled={starting}>
                            {starting ? <CircularProgress size={20} /> : "Start refresh"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
